import { PaymentReceivedPlatformTypeEnum } from 'features/salesOrder/types';

export { PaymentReceivedPlatformTypeEnum };

/**
 * Tipo de documento fiscal.
 * - CONTADO (Cash): los anticipos libres de la orden se asignan automáticamente al crear.
 * - CREDITO (Credit): los anticipos se vinculan manualmente después de emitir.
 * - NOTA_CREDITO (CreditNote): anula total o parcialmente una factura ISSUED. Total negativo.
 */
export enum InvoiceTypeEnum {
	Cash = 'Cash',
	Credit = 'Credit',
	CreditNote = 'CreditNote',
}

/**
 * Estado de la factura.
 * - DRAFT: borrador, editable y eliminable.
 * - ISSUED: emitida (tiene externalReference + pdfUrl), de solo lectura.
 * - CREDITED: anulada por una Nota de Crédito, de solo lectura.
 */
export enum InvoiceStatusEnum {
	Draft = 'Draft',
	Issued = 'Issued',
	Credited = 'Credited',
}

export interface IInvoiceItem {
	id: string;
	invoiceId: string;
	/** FK opcional al servicio de la orden. */
	serviceId?: string;
	description: string;
	/** Neto sin IVA. El IVA es "hacia adentro": el importe que carga el operador es el total final. */
	unitPrice: number;
	quantity: number;
	/** unitPrice * quantity */
	subtotal: number;
	/** base imponible para IVA */
	taxableBase: number;
	/** porcentaje: 0, 10, 13, 19... */
	taxRate: number;
	/** taxableBase * (taxRate / 100) */
	taxAmount: number;
	/** subtotal + taxAmount */
	total: number;
	/** subtotal convertido a USD */
	subtotalInMainCurrency: number;
	/** total convertido a USD */
	totalInMainCurrency: number;
}

/** Versión liviana usada en listados (sin ítems). */
export interface IInvoiceSummary {
	id: string;
	/** Número INTERNO correlativo de mevuelo. Ej: "2026-00042". */
	invoiceNumber: string;
	saleOrderId: string;
	dealId: number;
	isBitrixDeal: boolean;
	type: InvoiceTypeEnum;
	status: InvoiceStatusEnum;
	passengerName: string;
	passengerDocument?: string;
	passengerIsPhysical: boolean;
	/** ISO 4217 */
	currency: string;
	subtotal: number;
	taxAmount: number;
	total: number;
	/** total convertido a USD (siempre presente) */
	totalInMainCurrency: number;
	/** SUM de recibos vinculados */
	coveredAmount: number;
	/** total - coveredAmount */
	pendingAmount: number;
	/** hoy − dueDate en días; negativo si aún no venció. */
	overdueDays?: number;
	/** YYYY-MM-DD */
	issueDate: string;
	/** YYYY-MM-DD */
	dueDate: string;
	/** tipo de cambio al momento de emisión */
	exchangeRate?: number;
	/** Número OFICIAL del facturador electrónico (CAE, Timbrado, CUFE). */
	externalReference?: string;
	clientReference?: string;
	pdfUrl?: string;
	notes?: string;
	agencyId: number;
	creditNoteForInvoiceId?: string;
	creditNoteForInvoiceNumber?: string;
	/** ISO timestamp */
	issuedAt?: string;
	/** email del usuario que emitió */
	issuedBy?: string;
}

/** Detalle de factura: incluye los ítems. */
export interface IInvoice extends IInvoiceSummary {
	items: IInvoiceItem[];
}

export interface IInvoiceFilters {
	saleOrderId?: string;
	type?: InvoiceTypeEnum[];
	status?: InvoiceStatusEnum[];
	passengerId?: string;
	/** Nro. de documento del pasajero facturable (match exacto). */
	passengerDocument?: string;
	agencyId?: number;
	/** YYYY-MM-DD */
	issueDateFrom?: string;
	/** YYYY-MM-DD */
	issueDateTo?: string;
	/** YYYY-MM-DD */
	dueDateFrom?: string;
	/** YYYY-MM-DD */
	dueDateTo?: string;
	/** excluye facturas con pendingAmount <= 0 */
	hasPendingBalance?: boolean;
	/** dueDate < HOY && pendingAmount > 0 && ISSUED */
	isOverdue?: boolean;
	/** facturas con saldo cuya orden está 100% cobrada (revisión semanal) */
	saleOrderFullyCharged?: boolean;
	/** texto libre */
	search?: string;
}

/** Parámetros de paginación/orden para el listado global. */
export interface IInvoiceListParams extends IInvoiceFilters {
	page?: number;
	size?: number;
	sort?: string;
}

/** GET /passengers/invoices/{id}/summary — saldos en USD; "por vencer" incluye lo que vence hoy. */
export interface IPassengerInvoiceSummary {
	totalPendingBalance: number;
	overduePendingBalance: number;
	notYetDuePendingBalance: number;
	totalPendingDocumentsCount: number;
	overdueDocumentsCount: number;
	notYetDueDocumentsCount: number;
}

export interface IInvoiceListResponse {
	page: number;
	size: number;
	/** total de registros para paginación */
	total: number;
	/** sin items[] por performance */
	elements: IInvoiceSummary[];
}

export interface ICreateInvoicePayload {
	saleOrderId: string;
	type: InvoiceTypeEnum;
	passengerId: string;
	/** Nombre/razón social del pasajero o empresa facturada — usado por el mock y sugerido al backend. */
	passengerName?: string;
	/** ISO 4217 */
	currency: string;
	/** YYYY-MM-DD */
	issueDate: string;
	/** YYYY-MM-DD */
	dueDate: string;
	/** tipo de cambio si currency != USD */
	exchangeRate?: number;
	items: Omit<IInvoiceItem, 'id' | 'invoiceId'>[];
	/** si se provee, la factura se emite directamente (ISSUED) */
	externalReference?: string;
	/** requerido si externalReference presente */
	pdfUrl?: string;
	clientReference?: string;
	notes?: string;
	/** solo NOTA_CREDITO */
	creditNoteForInvoiceId?: string;
	/** solo CONTADO */
	receiptIds?: string[];
}

/** Body de PATCH /invoices/{id}/issue */
export interface IIssueInvoicePayload {
	externalReference: string;
	pdfUrl?: string;
}

/** Body de PATCH /invoices/{id}/link-receipts */
export interface ILinkReceiptsPayload {
	receiptIds: string[];
}

/**
 * Recibo de cobro (anticipo). Refleja el modelo `receipts` existente al que
 * se le agrega `invoiceId`. Se usa para precargar/vincular anticipos a facturas.
 *
 * Regla: un recibo se asigna ENTERO a una sola factura.
 */
export interface IInvoiceReceipt {
	id: string;
	saleOrderId: string;
	/** ISO 4217 */
	currency: string;
	/** monto del cobro en la moneda original */
	transactionAmount: number;
	/** monto convertido a USD (referencia común multi-moneda) */
	amountInMainCurrency: number;
	/** monto convertido a moneda local, si existe */
	transactionAmountInLocalCurrency?: number | null;
	/** tasa usada para convertir USD a moneda local, si existe */
	rateToConvertToLocalCurrency?: number | null;
	platformType: PaymentReceivedPlatformTypeEnum;
	/** código de autorización del cobro (si aplica) */
	authorizationCode?: string;
	/** YYYY-MM-DD */
	receiptDate: string;
	/**
	 * FK opcional hacia invoice.id.
	 * undefined / null -> anticipo libre, disponible para asignar.
	 * string -> vinculado a una factura específica.
	 */
	invoiceId?: string | null;
}

export interface IInvoiceReceiptFilters {
	saleOrderId?: string;
	/** recibos vinculados a una factura concreta */
	invoiceId?: string;
	/** true = solo recibos sin invoiceId (anticipos libres) */
	unlinked?: boolean;
}
